'use client';

import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import type { AuthManualControlEventProps } from '@/models/analytics/features/user';
import { mapSignInMethodToAuthType } from '@/models/analytics/features/user';
import { useDesktopAuthStore, type ManualControlState } from '@/models/desktopAuthSlice';
import { AuthProvider } from '@/utils/auth/persistedAuthProvider';
import { logger } from '@/utils/logger/logger';

export type ManualControlAction = 'copy-link' | 'open-browser' | 'fallback';

type AuthManualControlEventName =
  | TrackingEventName.AuthManualLinkCopied
  | TrackingEventName.AuthManualOpenClicked
  | TrackingEventName.AuthFallbackUsed;

const EVENT_BY_ACTION: Record<ManualControlAction, AuthManualControlEventName> = {
  'copy-link': TrackingEventName.AuthManualLinkCopied,
  'open-browser': TrackingEventName.AuthManualOpenClicked,
  fallback: TrackingEventName.AuthFallbackUsed,
};

export function trackManualControlEvent(
  action: ManualControlAction,
  state: ManualControlState
): void {
  const eventName = EVENT_BY_ACTION[action];
  if (!eventName) {
    return;
  }

  const props: AuthManualControlEventProps = {
    auth_provider: AuthProvider.AUTH0_DESKTOP,
    attempt_id: state.attemptId,
    browser_name: state.browserName ?? 'unknown',
    has_auth_url: Boolean(state.authUrl),
    copy_count: state.copyCount,
    open_again_count: state.openAgainCount,
    fallback_count: state.fallbackCount,
    last_action: state.lastAction,
    launched_at: state.launchedAt,
    component: 'desktop_browser_notice',
    action_source: 'desktop_auth_webview',
  };

  // Best-effort: telemetry must not interrupt the login fallback flow.
  // All three manual-control events share AuthManualControlEventProps, so the
  // assertion is safe. A single cast avoids a redundant three-branch switch.
  try {
    track({
      event_name: eventName as TrackingEventName.AuthManualLinkCopied,
      props,
      options: { posthog: true },
    });
  } catch (error) {
    logger.warn('[Auth] Failed to track manual control event', { action, error });
  }
}

/**
 * Emits `user_signed_in` when the Auth0 login flow completes for a
 * returning user. Sign-ups are tracked separately via trackSignupSuccessful
 * during the onboarding flow.
 *
 * Should be called once on `auth:login-success`. Reads and clears loginIntent
 * from desktopAuthSlice to ensure the event fires exactly once per login.
 */
export function trackAuthSuccess(): void {
  const desktopAuthStore = useDesktopAuthStore.getState();
  const { loginIntent } = desktopAuthStore;

  if (loginIntent?.screenHint !== 'signup') {
    track({
      event_name: TrackingEventName.UserSignedIn,
      props: {
        auth_provider: AuthProvider.AUTH0_DESKTOP,
        sign_in_method: loginIntent?.signInMethod,
        provider: 'auth0',
        auth_type: mapSignInMethodToAuthType(loginIntent?.signInMethod),
      },
      options: { amplitude: false, posthog: true },
    });
  }

  desktopAuthStore.clearLoginIntent();
}
